/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `ChatMessage` for the Caintra Connect frontend.
 * @module components/Chat/ChatMessage
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useTranslation } from "react-i18next";
import { ReactComponent as DownloadIcon } from "../../assets/icons/Chat/DownloadIcon.svg";
import { ReactComponent as OpenFileIcon } from "../../assets/icons/Chat/OpenFileIcon.svg";
import { ReactComponent as FileIcon } from "../../assets/icons/Chat/FileIcon.svg";
import { ReactComponent as ImageIcon } from "../../assets/icons/Chat/ImageIcon.svg";
import {
  isFileExpired,
  getFileTypeAndIcon,
  getCurrentUserProfileImage,
  extractMessageTimestamp,
  formatMessageTimestamp,
} from "../../logic/chat";
import {
  decodeFileName,
  handleFileDownload,
  resolveAccessibleAssetUrl,
} from "../../utils/common";
import AssetImage from "../AssetImage";

const avatarFallbackStyle = {
  backgroundColor: "#d5def8",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 600,
  textTransform: "uppercase",
};

const FileMessageContent = ({ message, index, styles, t }) => {
  const { icon } = getFileTypeAndIcon(message.fileName, message.messageType);
  const fileExpired = isFileExpired(message.fileExpiresAt);

  return (
    <div className={styles.fileMessage}>
      <div className={styles.fileInfo}>
        <div className={styles.fileHeader}>
          {icon && (
            <div className={styles.fileIconContainer}>
              {icon === "image" ? (
                <ImageIcon className={styles.fileTypeIcon} />
              ) : icon === "file" ? (
                <FileIcon className={styles.fileTypeIcon} />
              ) : null}
            </div>
          )}
          <div className={styles.fileDetails}>
            <span className={styles.fileName}>
              {decodeFileName(message.fileName)}
            </span>
          </div>
        </div>
        {fileExpired && (
          <div className={styles.fileExpiredMessage}>
            {t("pages.chat.fileExpired")}
          </div>
        )}
      </div>
      {!fileExpired && (
        <div
          className={`${styles.fileActions} ${
            index === 0 ? styles.fileActionsFirstMessage : ""
          }`}
        >
          <button
            className={styles.downloadButton}
            onClick={() =>
              handleFileDownload(message.fileKey || message.fileUrl, message.message)
            }
            title={t("pages.chat.downloadFile")}
          >
            <DownloadIcon className={styles.downloadIcon} />
          </button>
          <button
            className={styles.openFileButton}
            onClick={async () => {
              const url = await resolveAccessibleAssetUrl(
                message.fileKey || message.fileUrl,
              );
              if (url) {
                window.open(url, "_blank");
              }
            }}
            title={t("pages.chat.openFileInNewTab")}
          >
            <OpenFileIcon className={styles.openFileIcon} />
          </button>
        </div>
      )}
    </div>
  );
};

const MessageAvatar = ({ profileImage, alt, fallbackInitial, styles }) => {
  if (profileImage) {
    return (
      <AssetImage
        src={profileImage}
        alt={alt}
        className={styles.avatarImage}
      />
    );
  }

  return (
    <div className={styles.avatarImage} style={avatarFallbackStyle}>
      {fallbackInitial}
    </div>
  );
};

const ChatMessage = ({ message, index, users, styles }) => {
  const { t } = useTranslation();
  const currentUserId = localStorage.getItem("userId");
  const isSender = message.senderUserId === currentUserId;
  const messageClass = isSender ? styles.senderMessage : styles.receiverMessage;
  const senderUser = users.find((user) => user._id === message.senderUserId);
  const senderProfileImage = isSender
    ? getCurrentUserProfileImage(users)
    : senderUser?.imagenDePerfil || null;
  const displayTimestamp = extractMessageTimestamp(message);
  const currentUser = users.find((u) => u._id === currentUserId);

  return (
    <div className={styles.messageWrapper}>
      <div
        className={`${styles.messageContainer} ${
          isSender ? styles.senderContainer : styles.receiverContainer
        }`}
      >
        {!isSender && (
          <div className={styles.receiverAvatar}>
            <MessageAvatar
              profileImage={senderProfileImage}
              alt={t("common.userAvatarAlt", {
                name: senderUser?.nombre || t("common.defaultUserName"),
              })}
              fallbackInitial={(senderUser?.nombre || "U").charAt(0)}
              styles={styles}
            />
          </div>
        )}
        <div
          className={
            isSender ? styles.bubbleColumnSender : styles.bubbleColumnReceiver
          }
        >
          <div className={`${styles.message} ${messageClass}`}>
            {message.messageType === "FILE" ? (
              <FileMessageContent
                message={message}
                index={index}
                styles={styles}
                t={t}
              />
            ) : (
              <span className={styles.messageText}>{message.message}</span>
            )}
          </div>
          <div
            className={`${styles.messageTimestamp} ${
              isSender ? styles.senderTimestamp : styles.receiverTimestamp
            }`}
          >
            {formatMessageTimestamp(displayTimestamp)}
          </div>
        </div>
        {isSender && (
          <div className={styles.senderAvatar}>
            <MessageAvatar
              profileImage={senderProfileImage}
              alt={t("common.yourAvatarAlt")}
              fallbackInitial={(currentUser?.nombre || "Y").charAt(0)}
              styles={styles}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
